
import {combineLatest, empty as observableEmpty, of as observableOf, Subject, Observable} from 'rxjs';

import {map, mergeMap} from 'rxjs/operators';
import {Component, Input} from '@angular/core';
import {DeviceSlotId, DeviceSlotReservationId, DeviceTemplateId, ProjectId} from "../../types/ids";
import {IResourceSummaryComponent} from "../resources/resource-summary.component";
import {ActivatedRoute} from "@angular/router";
import {WebmateEvent} from "../../types/base-types";
import {
    AbsoluteDeviceSlotReservation, DeviceSlotReservation,
    ScheduledDeviceSlotReservation
} from "../../types/devices/device-slot-reservation";
import {DeviceSlotReservationService} from "../../services/device/device-slot-reservation.service";
import {UserService} from "../../services/user.service";
import {DeviceSlotService} from "../../services/device/device-slot.service";
import {DeviceSlot} from "../../types/devices/device-slot";
import {UserInfo} from "../../types/user/user-info";
import {
    CreateOrDeleteOwnSlotReservationsPermission,
    ManageSlotReservationsPermission, ProjectAdmin
} from '../../constants/permission.constants';
import {isDefined} from "../../utils/utils";
import {WmNotificationService} from "@webmate-web-core/shared/components/notification/wm-notification.service";
import moment from 'moment';


@Component({
    selector: 'wm-slot-reservation-summary',
    templateUrl: './slot-reservation-summary.component.html',
    styleUrls: ['./slot-reservation-summary.component.less']
})
export class SlotReservationSummaryComponent implements IResourceSummaryComponent {

    genericEvent: Subject<WebmateEvent>;

    @Input() resourceType: string;
    @Input() resourceId: DeviceSlotReservationId;
    @Input() projectId: ProjectId;
    @Input() viewType: string; //not supported!
    @Input() params: Map<DeviceSlotReservationId, DeviceSlotId>; //maps reservationIds to SlotIds

    reservationLoaded: boolean = false;
    slot: DeviceSlot = undefined;
    user: UserInfo = undefined;

    mayCancelThisReserv = false;
    isMyOwnReserv = false;
    isBusy = false;

    /** our instance-issue is resolved through multiple variables, each with their own type. Note that only one of them is ever defined! */
    absReservation: AbsoluteDeviceSlotReservation = undefined;
    schedReservation: ScheduledDeviceSlotReservation = undefined;
    //private something: Something   [=>  add new types of Reservations here  <=]

    constructor(private reservationService: DeviceSlotReservationService, private slotService: DeviceSlotService, private userService: UserService, private notificationService: WmNotificationService) {}

    ngOnInit() {
        let castedParam: Map<DeviceSlotReservationId, DeviceSlotId> = this.params;
        let reserv$ = this.reservationService.getReservationForSlot(this.resourceId, castedParam.get(this.resourceId), this.projectId);
        reserv$.subscribe(next => {
            /** first, reset all */
            this.absReservation = undefined;
            this.schedReservation = undefined;

            if (isDefined(next)){
                /** then, set the one that we have */
                if (next instanceof AbsoluteDeviceSlotReservation){
                    this.absReservation = next;
                } else if (next instanceof ScheduledDeviceSlotReservation){
                    this.schedReservation = next;
                }

                /** finally, set our boolean */
                this.reservationLoaded = true;
            } else this.reservationLoaded = false;
        });
        let thereIsAReserv: Observable<DeviceSlotReservation> = reserv$.pipe(mergeMap(maybeReserv => isDefined(maybeReserv) ? observableOf(maybeReserv) : observableEmpty()));
        thereIsAReserv.pipe(mergeMap( reser => this.slotService.getSlotInProject$(reser.slotId, this.projectId))).subscribe( next => this.slot = next);
        thereIsAReserv.pipe(mergeMap( reser => this.userService.getInformationAboutUser(reser.userId))).subscribe( next => this.user = next);
        thereIsAReserv.pipe(mergeMap( reserv => this.userService.getCurrentUserData().pipe(mergeMap( myself => {
            this.isMyOwnReserv = (myself.id === reserv.userId);
            return this.isMyOwnReserv
                ? this.userService.hasPermissionInProject(this.projectId, CreateOrDeleteOwnSlotReservationsPermission)
                : combineLatest([
                    this.userService.hasPermissionInProject(this.projectId, ProjectAdmin),
                    this.userService.hasAdminPermission(ManageSlotReservationsPermission)
                ]).pipe(map(([hasProjectAdminPermission, hasManageSlotReservationsPermission]) => (reserv.projectId !== '00000000-0000-0000-0000-000000000000' && hasProjectAdminPermission) || hasManageSlotReservationsPermission));
        })))).subscribe(permission => this.mayCancelThisReserv = permission);
    }

    cancelSlotReservation(){
        let request = () => {
            this.isBusy = true;
            this.reservationService.removeReservationFromSlot(this.resourceId, this.slot.id).subscribe({
                next: () => {
                    // no need to reset 'isBusy', Reservation should be removed soon via Event
                },
                error: err => {
                    this.isBusy = false;
                    this.notificationService.toastErrorCodeNotification(err.error.message);
                }
            });
        };
        this.notificationService.open(this.isMyOwnReserv ? "Are you sure that you want to cancel your reservation?" : "Cancelling this reservation might interfere with "+this.user.name+"'s work during the reserved time frame. Do you want to continue?", "Yes, continue", request, "Abort");
    }

    get shortenedSlotId(): string {
        if (!this.slot?.id || this.slot.id.length <= 8) {
            return this.slot?.id ?? '';
        }
        return `${this.slot.id.substring(0, 4)}...${this.slot.id.substring(this.slot.id.length - 4)}`;
    }

    get absoluteDuration(): string {
        if (!this.absReservation) {
            return '';
        }
        const durationInMinutes = Math.round((new Date(this.absReservation.end).getTime() - new Date(this.absReservation.begin).getTime()) / 60000);
        return this.formatDuration(durationInMinutes);
    }

    get absoluteReservationIsOnSingleDay(): boolean {
        if (!this.absReservation) {
            return false;
        }
        return moment(this.absReservation.begin).isSame(moment(this.absReservation.end), 'day');
    }

    get reservationDuration(): string {
        if (this.absReservation) {
            return this.absoluteDuration;
        }
        if (this.schedReservation?.schedule.optEventDuration) {
            return this.formatDuration(Math.round(moment.duration(this.schedReservation.schedule.optEventDuration).asMinutes()));
        }
        return '';
    }

    private formatDuration(durationInMinutes: number): string {
        if (durationInMinutes < 60) {
            return `${durationInMinutes} min`;
        }
        const hours = Math.floor(durationInMinutes / 60);
        const minutes = durationInMinutes % 60;
        return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
    }
}
